/**
 * lazyRetry — drop-in replacement for React.lazy() that retries a failed
 * dynamic import up to N times with exponential-ish backoff before giving
 * up. Prevents the "blank screen after slow network" bug where a lazy
 * chunk fetch fails once and never recovers.
 *
 * Usage:
 *   const Profile = lazyRetry(() => import('@/pages/Profile'));
 *
 * Behavior:
 *   - 1st failure  → wait 500ms, retry
 *   - 2nd failure  → wait 1500ms, retry
 *   - 3rd failure  → reject → ErrorBoundary catches ChunkLoadError → reload
 */
import { lazy } from 'react';

const DEFAULT_RETRIES = 2;
const RETRY_DELAYS_MS = [500, 1500];

export function retryImport(importer, retries = DEFAULT_RETRIES) {
  return new Promise((resolve, reject) => {
    const attempt = (remaining) => {
      importer()
        .then(resolve)
        .catch((err) => {
          if (remaining <= 0) {
            reject(err);
            return;
          }
          const idx = DEFAULT_RETRIES - remaining;
          const wait = RETRY_DELAYS_MS[idx] ?? 1500;
          console.warn(`[lazyRetry] chunk load failed, retrying in ${wait}ms (${remaining} left)`, err?.message);
          setTimeout(() => attempt(remaining - 1), wait);
        });
    };
    attempt(retries);
  });
}

export default function lazyRetry(importer) {
  return lazy(() => retryImport(importer));
}
